/** Shell header + document title for dashboard routes (labels align with sidebar nav). */
export const STORE_ADMIN_APP_NAME = 'Kartly Store Admin'

export type PageTitleEntry = { title: string; subtitle: string }

/** Exact path → header copy for primary sidebar destinations. */
export const dashboardPageTitles: Record<string, PageTitleEntry> = {
  '/': { title: 'Dashboard', subtitle: 'Metrics and quick actions' },
  '/categories': { title: 'Categories', subtitle: 'Store category presets' },
  '/products': { title: 'Products', subtitle: 'Manage your catalog' },
  '/special-offers': { title: 'Special offers', subtitle: 'Scheduled discounts and deal membership' },
  '/orders': { title: 'Orders', subtitle: 'Monitor and update orders' },
  '/customers': { title: 'Customers', subtitle: 'Shopper profiles and order history' },
  '/discount-codes': {
    title: 'Discount codes',
    subtitle: 'Checkout promo codes customers apply at payment',
  },
  '/transactions': { title: 'Transactions', subtitle: 'Payment and settlement history' },
  '/analytics': { title: 'Analytics', subtitle: 'Revenue trends and CSV exports' },
  '/commerce': { title: 'Payments', subtitle: 'Wallet, gateways, and checkout settings' },
  '/pickup-locations': {
    title: 'Pickup locations',
    subtitle: 'Pickup points shoppers choose at checkout',
  },
  '/withdrawals': { title: 'Withdrawals', subtitle: 'Request payouts and track withdrawal status' },
  '/storefront': { title: 'Store profile', subtitle: 'Branding and public store details' },
  '/homepage-sections': {
    title: 'Homepage sections',
    subtitle: 'Merchandising blocks on your storefront homepage',
  },
  '/newsletter-subscribers': { title: 'Newsletter', subtitle: 'Storefront newsletter subscribers' },
  '/contact-messages': { title: 'Messages', subtitle: 'Storefront contact form inbox' },
  '/team': { title: 'Team', subtitle: 'Staff access to store admin' },
  '/billing': { title: 'Subscription', subtitle: 'Plan, billing dates, and payment method' },
  '/settings': { title: 'General', subtitle: 'Store admin preferences' },
  '/pos/sales': { title: 'Point of Sale', subtitle: 'In-store checkout' },
  '/pos/history': { title: 'Sales history', subtitle: 'POS sales by day' },
}

export function matchDashboardPageTitle(pathname: string): PageTitleEntry {
  if (dashboardPageTitles[pathname]) return dashboardPageTitles[pathname]
  if (pathname.startsWith('/billing')) {
    return dashboardPageTitles['/billing']
  }
  if (pathname.startsWith('/products/new')) {
    return { title: 'Add product', subtitle: 'Create product listing' }
  }
  if (pathname.match(/^\/products\/[^/]+\/edit$/)) {
    return { title: 'Edit product', subtitle: 'Update product details' }
  }
  if (pathname.match(/^\/products\/[^/]+$/)) {
    return { title: 'Product details', subtitle: 'View product information' }
  }
  if (pathname.match(/^\/orders\/[^/]+$/)) {
    return { title: 'Order details', subtitle: 'Review and update order state' }
  }
  if (pathname.match(/^\/customers\/[^/]+$/)) {
    return { title: 'Customer details', subtitle: 'Profile, addresses, and orders' }
  }
  if (pathname.match(/^\/transactions\/[^/]+$/)) {
    return { title: 'Transaction details', subtitle: 'View payment information' }
  }
  if (pathname.match(/^\/special-offers\/[^/]+\/products$/)) {
    return { title: 'Offer products', subtitle: 'Search catalog and manage deal membership' }
  }
  if (pathname.match(/^\/categories\/[^/]+$/)) {
    return { title: 'Category details', subtitle: 'View category and assigned products' }
  }
  if (pathname.match(/^\/homepage-sections\/[^/]+\/products$/)) {
    return {
      title: 'Section products',
      subtitle: 'Add or remove products from this homepage section',
    }
  }
  return { title: 'Dashboard', subtitle: '' }
}

export function formatDocumentTitle(pageTitle: string): string {
  return `${pageTitle} · ${STORE_ADMIN_APP_NAME}`
}
